/* LouVisualBoards — unified hub with 4 board types under Lou.
 * Tabs: 👗 Outfits · 📌 Mood · 🌈 Vision · 📷 Matcha
 */
import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { X, Loader2, Plus, Trash2, Wand2, Upload, Link as LinkIcon, Save, Check } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const TABS = [
  { id: 'outfit',   label: '👗 Outfits',   desc: 'Bygg outfits från din kapselgarderob' },
  { id: 'occasion', label: '🎯 Tillfällen', desc: 'AI-förslag för bröllop, vardag, fest…' },
  { id: 'mood',     label: '📌 Mood',      desc: 'Pinterest-stil inspirations-grid' },
  { id: 'vision',   label: '🌈 Vision',    desc: 'AI-collage av din estetik' },
  { id: 'match',    label: '📷 Matcha',    desc: 'Är detta plagget?' },
];

const OCCASIONS = ['Bröllop', 'Fest', 'Vardag', 'Jobbet', 'Jobbintervju', 'Date', 'Middag ute', 'Promenad', 'Resa', 'Strand', 'Begravning', 'Träning', 'Julbord'];

const OUTFIT_SLOTS = ['top', 'bottom', 'jacket', 'shoes', 'bag', 'accessory'];
const SLOT_LABELS = { top: '👚 Topp', bottom: '👖 Underdel', jacket: '🧥 Jacka', shoes: '👞 Sko', bag: '👜 Väska', accessory: '✨ Accessoar' };

// ========== OUTFITS TAB ==========
function OutfitsTab({ capsule }) {
  const [outfits, setOutfits] = useState([]);
  const [name, setName] = useState('');
  const [occasion, setOccasion] = useState('');
  const [slots, setSlots] = useState({}); // slot → capsule_index
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/experts/lou/outfits`, { withCredentials: true });
      setOutfits(r.data?.items || []);
    } catch {/**/}
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!name.trim()) { toast.error('Ge outfiten ett namn 💜'); return; }
    setBusy(true);
    try {
      const slotsList = Object.entries(slots).map(([slot, idx]) => ({ slot, capsule_index: idx }));
      await axios.post(`${API}/experts/lou/outfits`, { name, occasion, slots: slotsList }, { withCredentials: true });
      toast.success('👗 Outfit sparad');
      setName(''); setOccasion(''); setSlots({});
      load();
    } catch (e) { toast.error('Kunde inte spara'); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Ta bort outfiten?')) return;
    try {
      await axios.delete(`${API}/experts/lou/outfits/${id}`, { withCredentials: true });
      load();
    } catch {/**/}
  };

  if (!capsule) {
    return <div className="text-center py-12 text-stone-500 text-sm">
      🧺 Skapa en kapselgarderob först — då kan du kombinera plaggen till outfits här
    </div>;
  }

  const items = capsule.items || [];

  return (
    <div className="space-y-4" data-testid="outfits-tab">
      {/* Builder */}
      <section className="rounded-2xl bg-white border border-stone-200 p-3 space-y-2">
        <p className="text-[10px] uppercase tracking-widest font-extrabold text-stone-600">Bygg outfit</p>
        <input
          value={name}
          onChange={(e) => setName(e.target.value.slice(0, 40))}
          placeholder="Namn (t.ex. Måndag-jobbet)"
          data-testid="outfit-name-input"
          className="w-full px-3 py-2 rounded-xl border-2 border-stone-200 focus:border-violet-400 outline-none text-sm"
        />
        <input
          value={occasion}
          onChange={(e) => setOccasion(e.target.value.slice(0, 60))}
          placeholder="Tillfälle (valfritt — t.ex. middag med Mia)"
          data-testid="outfit-occasion-input"
          className="w-full px-3 py-2 rounded-xl border-2 border-stone-200 focus:border-violet-400 outline-none text-sm"
        />
        <div className="space-y-2">
          {OUTFIT_SLOTS.map((slot) => {
            const picked = slots[slot];
            return (
              <div key={slot} className="rounded-xl bg-stone-50 border border-stone-200 p-2">
                <p className="text-[10px] uppercase tracking-widest font-extrabold text-stone-500 mb-1">{SLOT_LABELS[slot]}</p>
                <div className="flex gap-1 overflow-x-auto pb-1">
                  {items.map((it, idx) => (
                    <button
                      key={idx}
                      onClick={() => setSlots((s) => ({ ...s, [slot]: picked === idx ? undefined : idx }))}
                      data-testid={`outfit-pick-${slot}-${idx}`}
                      className={`shrink-0 px-2 py-1 rounded-lg text-[10px] font-bold border-2 transition ${
                        picked === idx ? 'bg-violet-600 border-violet-600 text-white' : 'bg-white border-stone-200 text-stone-700 hover:border-violet-300'
                      }`}
                      title={`${it.slot}: ${it.color || ''}`}
                    >
                      {it.name.slice(0, 24)}
                    </button>
                  ))}
                </div>
              </div>
            );
          })}
        </div>
        <button
          onClick={save}
          disabled={busy || !name.trim() || Object.keys(slots).length === 0}
          data-testid="outfit-save-btn"
          className="w-full py-2.5 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Spara outfit
        </button>
      </section>

      {/* Saved outfits */}
      <section className="space-y-2">
        <p className="text-[10px] uppercase tracking-widest font-extrabold text-stone-600">Sparade outfits ({outfits.length})</p>
        {outfits.length === 0 && <p className="text-xs italic text-stone-500 text-center py-4">Inga sparade outfits ännu 💜</p>}
        {outfits.map((o) => (
          <div key={o.id} className="rounded-2xl bg-white border border-stone-200 p-3" data-testid={`outfit-card-${o.id}`}>
            <div className="flex items-start justify-between gap-2">
              <div className="flex-1 min-w-0">
                <p className="font-extrabold text-stone-800 text-sm leading-tight">{o.name}</p>
                {o.occasion && <p className="text-[11px] italic text-stone-500">{o.occasion}</p>}
              </div>
              <button onClick={() => remove(o.id)} className="p-1 rounded-full text-rose-500 hover:bg-rose-50" data-testid={`outfit-delete-${o.id}`}>
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
            <div className="flex flex-wrap gap-1 mt-1.5">
              {(o.slots || []).map((s, i) => {
                const it = items[s.capsule_index];
                if (!it) return null;
                return (
                  <span key={i} className="text-[10px] bg-violet-50 border border-violet-200 text-violet-700 px-2 py-0.5 rounded-full">
                    {SLOT_LABELS[s.slot] || s.slot}: {it.name.slice(0, 20)}
                  </span>
                );
              })}
            </div>
          </div>
        ))}
      </section>
    </div>
  );
}

// ========== OCCASION TAB ==========
function OccasionTab({ capsule }) {
  const [occasion, setOccasion] = useState('');
  const [custom, setCustom] = useState('');
  const [outfits, setOutfits] = useState([]);
  const [busy, setBusy] = useState(false);
  const [packs, setPacks] = useState([]);

  const loadPacks = async () => {
    try {
      const r = await axios.get(`${API}/experts/lou/occasion-outfits`, { withCredentials: true });
      setPacks(r.data?.packs || []);
    } catch {/**/}
  };
  useEffect(() => { loadPacks(); }, []);

  const generate = async (occ) => {
    const finalOcc = (occ || custom || '').trim();
    if (!finalOcc) { toast.error('Välj eller skriv ett tillfälle 💜'); return; }
    setBusy(true);
    setOccasion(finalOcc);
    setOutfits([]);
    try {
      const r = await axios.post(`${API}/experts/lou/occasion-outfits`, { occasion: finalOcc, count: 3 }, { withCredentials: true, timeout: 180000 });
      setOutfits(r.data?.outfits || []);
      toast.success(`🎯 ${r.data?.outfits?.length || 0} outfits för ${finalOcc} 💜`);
      loadPacks();
    } catch (e) {
      toast.error('Kunde inte generera: ' + (e?.response?.data?.detail || ''));
    } finally { setBusy(false); }
  };

  const showPack = (p) => {
    setOccasion(p.occasion);
    setOutfits(p.outfits || []);
  };

  if (!capsule) {
    return <div className="text-center py-12 text-stone-500 text-sm">
      🧺 Skapa en kapselgarderob först — då bygger AI förslag för olika tillfällen
    </div>;
  }

  const items = capsule.items || [];

  return (
    <div className="space-y-3" data-testid="occasion-tab">
      <section className="rounded-2xl bg-white border border-stone-200 p-3 space-y-2">
        <p className="text-[10px] uppercase tracking-widest font-extrabold text-stone-600">Välj tillfälle</p>
        <div className="flex flex-wrap gap-1">
          {OCCASIONS.map((o) => (
            <button
              key={o}
              onClick={() => generate(o)}
              disabled={busy}
              data-testid={`occasion-chip-${o.toLowerCase().replace(/\s+/g, '-')}`}
              className="text-[11px] font-bold px-2.5 py-1 rounded-full border-2 border-violet-200 bg-white text-violet-700 hover:bg-violet-50 disabled:opacity-50"
            >
              {o}
            </button>
          ))}
        </div>
        <div className="flex gap-2">
          <input
            value={custom}
            onChange={(e) => setCustom(e.target.value.slice(0, 60))}
            placeholder="Eget tillfälle (t.ex. ”studentfest”)"
            data-testid="occasion-custom-input"
            className="flex-1 px-3 py-2 rounded-xl border-2 border-stone-200 focus:border-violet-400 outline-none text-sm"
          />
          <button
            onClick={() => generate(custom)}
            disabled={busy || !custom.trim()}
            data-testid="occasion-generate-btn"
            className="px-3 py-2 rounded-xl bg-violet-600 hover:bg-violet-700 text-white font-bold text-xs disabled:opacity-50"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
          </button>
        </div>
        {busy && (
          <p className="text-[11px] italic text-violet-600 text-center pt-1">
            Lou bygger 3 outfits + flat-lay-bilder för &quot;{occasion}&quot;… (~60-90 sek)
          </p>
        )}
      </section>

      {/* Generated outfits */}
      {outfits.length > 0 && (
        <section className="space-y-2.5" data-testid="occasion-results">
          <p className="text-[10px] uppercase tracking-widest font-extrabold text-stone-600">
            {occasion} · {outfits.length} förslag
          </p>
          {outfits.map((o, i) => (
            <div key={i} className="rounded-2xl bg-white border border-stone-200 overflow-hidden shadow" data-testid={`occasion-outfit-${i}`}>
              {o.image_base64 && (
                <img
                  src={`data:${o.image_mime || 'image/png'};base64,${o.image_base64}`}
                  alt={o.name}
                  className="w-full h-auto block"
                  data-testid={`occasion-outfit-image-${i}`}
                />
              )}
              <div className="p-3 space-y-1.5">
                <p className="font-serif text-lg font-bold text-stone-800 leading-tight">{o.name}</p>
                {o.vibe && <p className="text-[11px] italic text-stone-500">{o.vibe}</p>}
                <div className="flex flex-wrap gap-1 pt-1">
                  {(o.capsule_indices || []).map((idx) => {
                    const it = items[idx];
                    if (!it) return null;
                    return (
                      <span key={idx} className="text-[10px] bg-violet-50 border border-violet-200 text-violet-700 px-2 py-0.5 rounded-full">
                        🧺 {it.name.slice(0, 24)}
                      </span>
                    );
                  })}
                  {(o.extras || []).map((ex, ei) => (
                    <span key={`ex-${ei}`} className="text-[10px] bg-amber-50 border border-amber-200 text-amber-700 px-2 py-0.5 rounded-full">
                      ✚ {ex.name} ({ex.color})
                    </span>
                  ))}
                </div>
              </div>
            </div>
          ))}
        </section>
      )}

      {/* Previously generated packs */}
      {packs.length > 0 && outfits.length === 0 && (
        <section className="space-y-1.5">
          <p className="text-[10px] uppercase tracking-widest font-extrabold text-stone-600">Tidigare genererade</p>
          {packs.map((p, i) => (
            <button
              key={i}
              onClick={() => showPack(p)}
              data-testid={`occasion-pack-${i}`}
              className="w-full text-left rounded-xl bg-white border border-stone-200 hover:border-violet-400 px-3 py-2 text-sm font-bold text-stone-700 flex items-center justify-between"
            >
              <span>🎯 {p.occasion}</span>
              <span className="text-[10px] font-normal text-stone-500">{p.outfits?.length || 0} outfits</span>
            </button>
          ))}
        </section>
      )}
    </div>
  );
}

// ========== MOOD TAB ==========
function MoodTab() {
  const [items, setItems] = useState([]);
  const [url, setUrl] = useState('');
  const [caption, setCaption] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/experts/lou/mood-items`, { withCredentials: true });
      setItems(r.data?.items || []);
    } catch {/**/}
  };
  useEffect(() => { load(); }, []);

  const addUrl = async () => {
    if (!url.trim()) return;
    setBusy(true);
    try {
      await axios.post(`${API}/experts/lou/mood-items`, { image_url: url.trim(), caption }, { withCredentials: true });
      setUrl(''); setCaption('');
      load();
    } catch { toast.error('Kunde inte lägga till'); }
    finally { setBusy(false); }
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const reader = new FileReader();
      reader.onload = async () => {
        const b64 = String(reader.result).split(',')[1];
        await axios.post(`${API}/experts/lou/mood-items`, { image_b64: b64, caption }, { withCredentials: true });
        setCaption('');
        load();
        setBusy(false);
      };
      reader.readAsDataURL(file);
    } catch { setBusy(false); toast.error('Kunde inte ladda upp'); }
  };

  const remove = async (id) => {
    try {
      await axios.delete(`${API}/experts/lou/mood-items/${id}`, { withCredentials: true });
      load();
    } catch {/**/}
  };

  return (
    <div className="space-y-3" data-testid="mood-tab">
      <section className="rounded-2xl bg-white border border-stone-200 p-3 space-y-2">
        <p className="text-[10px] uppercase tracking-widest font-extrabold text-stone-600">Lägg till inspiration</p>
        <input
          value={caption}
          onChange={(e) => setCaption(e.target.value.slice(0, 80))}
          placeholder="Bildtext (valfritt)"
          data-testid="mood-caption-input"
          className="w-full px-3 py-2 rounded-xl border-2 border-stone-200 focus:border-violet-400 outline-none text-sm"
        />
        <div className="flex gap-2">
          <input
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            placeholder="Klistra in bild-URL"
            data-testid="mood-url-input"
            className="flex-1 px-3 py-2 rounded-xl border-2 border-stone-200 focus:border-violet-400 outline-none text-xs"
          />
          <button onClick={addUrl} disabled={busy || !url.trim()} data-testid="mood-add-url" className="px-3 py-2 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-xs font-bold disabled:opacity-50">
            <LinkIcon className="w-3.5 h-3.5" />
          </button>
          <label className="px-3 py-2 rounded-xl bg-violet-100 hover:bg-violet-200 text-violet-700 text-xs font-bold cursor-pointer flex items-center" data-testid="mood-upload-label">
            <Upload className="w-3.5 h-3.5" />
            <input type="file" accept="image/jpeg,image/png,image/webp" onChange={upload} className="hidden" data-testid="mood-upload-input" />
          </label>
        </div>
      </section>

      <section>
        <p className="text-[10px] uppercase tracking-widest font-extrabold text-stone-600 mb-2">Pins ({items.length})</p>
        {items.length === 0 && <p className="text-xs italic text-stone-500 text-center py-6">Klistra in en Pinterest-länk eller ladda upp en bild 💜</p>}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5" data-testid="mood-grid">
          {items.map((m) => {
            const src = m.image_b64 ? `data:image/png;base64,${m.image_b64}` : m.image_url;
            return (
              <div key={m.id} className="relative group rounded-xl overflow-hidden bg-stone-100 aspect-[3/4]" data-testid={`mood-pin-${m.id}`}>
                {src && <img src={src} alt={m.caption || 'pin'} loading="lazy" className="w-full h-full object-cover" onError={(e) => { e.target.style.display = 'none'; }} />}
                {m.caption && <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent px-2 py-1 text-[10px] text-white">{m.caption}</div>}
                <button onClick={() => remove(m.id)} className="absolute top-1 right-1 w-6 h-6 rounded-full bg-white/90 hover:bg-rose-100 text-rose-500 flex items-center justify-center opacity-0 group-hover:opacity-100 transition" data-testid={`mood-delete-${m.id}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            );
          })}
        </div>
      </section>
    </div>
  );
}

// ========== VISION TAB ==========
function VisionTab() {
  const [vision, setVision] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState('');

  const load = async () => {
    try {
      const r = await axios.get(`${API}/experts/lou/vision-board`, { withCredentials: true });
      setVision(r.data?.vision_board || null);
    } catch {/**/}
  };
  useEffect(() => { load(); }, []);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await axios.post(`${API}/experts/lou/vision-board`, { notes }, { withCredentials: true, timeout: 90000 });
      setVision({ image_base64: r.data.image_base64, image_mime: r.data.image_mime });
      toast.success('🌈 Din vision-board är klar 💜');
    } catch (e) {
      toast.error('Kunde inte generera: ' + (e?.response?.data?.detail || ''));
    } finally { setBusy(false); }
  };

  return (
    <div className="space-y-3" data-testid="vision-tab">
      <section className="rounded-2xl bg-white border border-stone-200 p-3 space-y-2">
        <p className="text-[10px] uppercase tracking-widest font-extrabold text-stone-600">Stil-vision (AI-collage)</p>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value.slice(0, 200))}
          placeholder="Extra mood (valfritt) — t.ex. lugn, biblioteks-känsla, höstliga rosor…"
          rows={2}
          data-testid="vision-notes-input"
          className="w-full px-3 py-2 rounded-xl border-2 border-stone-200 focus:border-violet-400 outline-none text-sm resize-none"
        />
        <button
          onClick={generate}
          disabled={busy}
          data-testid="vision-generate-btn"
          className="w-full py-2.5 rounded-xl bg-gradient-to-r from-violet-600 to-rose-500 hover:from-violet-700 hover:to-rose-600 text-white font-bold text-sm flex items-center justify-center gap-2 disabled:opacity-50"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wand2 className="w-4 h-4" />}
          {busy ? 'AI bygger din vision… (~30 sek)' : (vision ? '🔁 Generera ny vision' : '✨ Skapa min vision-board')}
        </button>
      </section>
      {vision?.image_base64 && (
        <div className="rounded-2xl overflow-hidden bg-white border border-stone-200 shadow">
          <img
            src={`data:${vision.image_mime || 'image/png'};base64,${vision.image_base64}`}
            alt="Stil-vision"
            className="w-full h-auto block"
            data-testid="vision-image"
          />
        </div>
      )}
    </div>
  );
}

// ========== MATCH TAB ==========
function MatchTab({ capsule }) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const fileRef = useRef(null);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    setResult(null);
    setPreviewUrl(URL.createObjectURL(file));
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await axios.post(`${API}/experts/lou/match-item`, fd, { withCredentials: true, timeout: 90000 });
      setResult(r.data);
    } catch (e) {
      toast.error('Kunde inte matcha: ' + (e?.response?.data?.detail || ''));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  if (!capsule) {
    return <div className="text-center py-12 text-stone-500 text-sm">
      🧺 Skapa en kapselgarderob först — då kan AI jämföra dina IRL-fynd
    </div>;
  }

  const verdictColor = result?.verdict === 'Köp' ? 'bg-emerald-500'
                     : result?.verdict === 'Skippa' ? 'bg-rose-500'
                     : 'bg-amber-500';

  return (
    <div className="space-y-3" data-testid="match-tab">
      <section className="rounded-2xl bg-white border border-stone-200 p-3 space-y-2">
        <p className="text-[10px] uppercase tracking-widest font-extrabold text-stone-600">Är detta plagget?</p>
        <p className="text-[11px] text-stone-500 italic">Ladda upp en bild på något du funderar på att köpa — AI jämför med din kapselgarderob.</p>
        <label className="block w-full py-3 rounded-xl border-2 border-dashed border-violet-300 hover:bg-violet-50 cursor-pointer text-center text-violet-700 font-bold text-sm" data-testid="match-upload-label">
          <Upload className="w-4 h-4 inline mr-1" /> Ladda upp plagg-foto
          <input ref={fileRef} type="file" accept="image/jpeg,image/png,image/webp" onChange={upload} className="hidden" data-testid="match-upload-input" />
        </label>
      </section>

      {busy && (
        <div className="text-center py-6 text-violet-600">
          <Loader2 className="w-8 h-8 animate-spin mx-auto" />
          <p className="text-xs italic mt-2">Lou analyserar plagget…</p>
        </div>
      )}

      {previewUrl && !busy && (
        <div className="grid grid-cols-2 gap-2">
          <div className="rounded-2xl overflow-hidden bg-white border border-stone-200">
            <img src={previewUrl} alt="Ditt plagg" className="w-full aspect-square object-cover" />
            <p className="text-[10px] uppercase font-bold text-stone-500 px-2 py-1 text-center">Ditt plagg</p>
          </div>
          {result?.best_match_item && (
            <div className="rounded-2xl bg-white border-2 border-violet-300 p-2">
              <p className="text-[9px] uppercase font-bold text-violet-600 mb-1">Närmaste i kapseln</p>
              <p className="font-bold text-stone-800 text-xs leading-tight">{result.best_match_item.name}</p>
              <p className="text-[10px] italic text-stone-500">{result.best_match_item.color}</p>
            </div>
          )}
        </div>
      )}

      {result && !busy && (
        <div className="rounded-2xl bg-white border border-stone-200 p-3 space-y-2" data-testid="match-result">
          <div className="flex items-center gap-2">
            <div className={`text-white font-extrabold px-3 py-1 rounded-full text-sm ${verdictColor}`} data-testid="match-verdict">
              {result.verdict === 'Köp' && <Check className="w-4 h-4 inline mr-1" />}
              {result.verdict}
            </div>
            <div className="flex-1 h-2 bg-stone-200 rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-rose-500 via-amber-500 to-emerald-500 transition-all" style={{ width: `${result.match_pct}%` }} />
            </div>
            <span className="text-sm font-extrabold text-stone-700" data-testid="match-pct">{result.match_pct}%</span>
          </div>
          <p className="text-xs text-stone-700 italic leading-snug" data-testid="match-reasoning">{result.reasoning}</p>
        </div>
      )}
    </div>
  );
}

// ========== MAIN HUB ==========
export default function LouVisualBoards({ onClose }) {
  const [tab, setTab] = useState('outfit');
  const [capsule, setCapsule] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/experts/lou/capsule`, { withCredentials: true });
        setCapsule(r.data?.capsule || null);
      } catch {/**/}
    })();
  }, []);

  return (
    <div className="fixed inset-0 z-[345] bg-black/75 backdrop-blur-md flex items-center justify-center p-2 sm:p-4" onClick={onClose} data-testid="visual-boards-modal">
      <div className="relative w-full max-w-3xl rounded-3xl overflow-hidden shadow-2xl bg-[#FAF6F0] max-h-[94vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <header className="px-4 py-3 bg-gradient-to-r from-stone-100 to-rose-50 border-b border-stone-200 flex items-center justify-between">
          <div>
            <p className="text-[10px] uppercase tracking-[0.3em] font-bold text-stone-500">Lou · stylist</p>
            <h2 className="font-serif text-2xl font-bold text-stone-800">Visual Boards 🎨</h2>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-full hover:bg-stone-200" data-testid="visual-boards-close">
            <X className="w-5 h-5 text-stone-700" />
          </button>
        </header>

        <nav className="flex gap-1 px-3 pt-3 overflow-x-auto bg-[#FAF6F0]" data-testid="visual-boards-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => setTab(t.id)}
              data-testid={`vb-tab-${t.id}`}
              className={`shrink-0 px-3 py-1.5 rounded-t-xl text-xs font-bold border-2 border-b-0 transition ${
                tab === t.id ? 'bg-white border-stone-300 text-stone-900' : 'bg-stone-100 border-transparent text-stone-500 hover:bg-white/60'
              }`}
            >
              {t.label}
            </button>
          ))}
        </nav>
        <p className="px-4 py-1 text-[10px] italic text-stone-500 bg-[#FAF6F0]">{TABS.find((t) => t.id === tab)?.desc}</p>

        <div className="flex-1 overflow-y-auto p-3 bg-white border-t border-stone-200">
          {tab === 'outfit'   && <OutfitsTab capsule={capsule} />}
          {tab === 'occasion' && <OccasionTab capsule={capsule} />}
          {tab === 'mood'     && <MoodTab />}
          {tab === 'vision'   && <VisionTab />}
          {tab === 'match'    && <MatchTab capsule={capsule} />}
        </div>
      </div>
    </div>
  );
}
